import React, { useState, type MouseEvent, type ReactNode } from 'react';
import clsx from 'clsx';

// ** MUI
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import type { IconButtonProps } from '@mui/material/IconButton';
import type { PopoverOrigin } from '@mui/material/Popover';

// ** Components
import FaIconUvilo, { type IconPrefix } from './FaIconUvilo';
import Spinner from './Spinner';
import Link, { type LinkProps } from 'next/link';

// ** Hooks
import { useTheme } from '@mui/material/styles';

// ** Utils
import { instantiateComponent } from '../shared/instantiateComponent';
import mergeSxProps from '../shared/mergeSxProps';
import _isNil from 'lodash/isNil';

export type MenuListItemType = {
  title: string;
  subHeader?: false;
  icon: string;
  hoverIcon?: string;

  /**
   * MUI button color. Applied to `PowerIconButton` when `standAloneButton` is
   * true, and to the primary menu icon as `{color}.main`.
   */
  color?: IconButtonProps['color'];
  prefix?: string;
  value: string;
  component?: React.ComponentType<unknown>;
  secondaryIcon?: string;
  onClick?: (event: MouseEvent, item?: MenuItemType) => void;
  href?: string;
  linkProps?: LinkProps;

  /**
   * When true, `ActionMenu` renders this entry as its own icon button (or nested
   * dropdown when `items` is set) instead of placing it under the More menu.
   */
  standAloneButton?: boolean;

  /**
   * Nested menu entries.
   * With `standAloneButton`, `ActionMenu` renders a standalone dropdown.
   * Without it, `MenuItems` / `DropdownMenu` render a cascading submenu.
   */
  items?: MenuItemType[];
  /** Selected value for nested / submenu checkmarks. */
  selectedItem?: string | null;
  disabled?: boolean;
  loading?: boolean;
  divider?: boolean;
  className?: string;
};

export type MenuSubHeaderType = {
  title: string;
  subHeader: true;
};

export type MenuItemType = MenuListItemType | MenuSubHeaderType;

export type MenuItemsProps = {
  items: MenuItemType[];
  onAfterClick: (event: MouseEvent) => void;
  selectedItem: string | null | undefined;
};

/**
 * Renders a flat or nested list of menu entries for `DropdownMenu` / `ActionMenu`.
 * @param props Component props.
 * @param props.items Entries to render; nested `items` become cascading submenus.
 * @param props.onAfterClick Called after a leaf item is activated (closes parents).
 * @param props.selectedItem Value that should show a checkmark in this menu level.
 * @returns Menu item nodes, including nested submenu menus when needed.
 */
export default function MenuItems(props: MenuItemsProps): ReactNode {
  'use memo';

  const { items = [], onAfterClick, selectedItem } = props;

  function handleItemClick(event: MouseEvent, item: MenuItemType): void {
    if (item.subHeader) return;
    event.stopPropagation();

    if (item.onClick) {
      item.onClick(event, item);
    }
    if (onAfterClick) {
      onAfterClick(event);
    }
  }

  return (
    <>
      {items.map((item, index) => {
        if (!item) return null;

        const selected = !_isNil(selectedItem) && !item.subHeader && item.value === selectedItem;
        if (selected && !item.subHeader && !item.secondaryIcon && !item.items?.length) {
          item.secondaryIcon = 'check';
        }

        if (item.subHeader) {
          return <ListSubheader key={index}>{item.title}</ListSubheader>;
        }

        if (item.items && item.items.length > 0) {
          return (
            <SubmenuMenuItem
              key={item.value}
              item={item}
              selected={selected}
              onAfterClick={onAfterClick}
            />
          );
        }

        if (item.component) {
          const componentProps = { ...item, key: index, onAfterClick };
          return instantiateComponent(item.component, componentProps);
        }

        return (
          <LeafMenuItem
            key={item.value}
            item={item}
            selected={selected}
            onClick={(event) => {
              handleItemClick(event, item);
            }}
          />
        );
      })}
    </>
  );
}

type LeafMenuItemProps = {
  item: MenuListItemType;
  selected: boolean;
  onClick: (event: MouseEvent) => void;
};

/**
 * Leaf menu entry with optional `hoverIcon` swap on hover / focus.
 */
function LeafMenuItem({ item, selected, onClick }: LeafMenuItemProps): ReactNode {
  'use memo';

  const [isActive, setIsActive] = useState(false);

  const baseMenuItemProps = {
    onClick,
    disabled: item.disabled,
    divider: item.divider,
    selected,
    onMouseEnter: () => {
      setIsActive(true);
    },
    onMouseLeave: () => {
      setIsActive(false);
    },
    onFocus: () => {
      setIsActive(true);
    },
    onBlur: () => {
      setIsActive(false);
    },
  };

  const content = <MenuItemContent item={item} showChevron={false} isActive={isActive} />;

  if (item.href) {
    return (
      <MenuItem
        {...baseMenuItemProps}
        className={clsx('leaf-menu-item', item.className)}
        component={Link}
        href={item.href}
        {...item.linkProps}
      >
        {content}
      </MenuItem>
    );
  }

  return (
    <MenuItem {...baseMenuItemProps} className={clsx('leaf-menu-item', item.className)}>
      {content}
    </MenuItem>
  );
}

type MenuItemContentProps = {
  item: MenuListItemType;
  showChevron: boolean;
  chevronIcon?: string;
  /** When true and `item.hoverIcon` is set, show the hover icon instead of `item.icon`. */
  isActive?: boolean;
};

/**
 * Shared icon + label row for leaf and submenu parent items.
 */
function MenuItemContent({
  item,
  showChevron,
  chevronIcon,
  isActive = false,
}: MenuItemContentProps): ReactNode {
  'use memo';

  const displayedIcon = item.hoverIcon && isActive ? item.hoverIcon : item.icon;
  const iconColor =
    item.color && item.color !== 'inherit' && item.color !== 'default'
      ? `${item.color}.main`
      : undefined;

  return (
    <>
      <ListItemIcon className="menu-item-content">
        {item.loading ? (
          <Spinner width={20} />
        ) : (
          <FaIconUvilo icon={displayedIcon} prefix={item.prefix as IconPrefix} color={iconColor} />
        )}
      </ListItemIcon>
      <ListItemText primary={item.title} />
      {item.secondaryIcon ? (
        <FaIconUvilo
          icon={item.secondaryIcon}
          sx={{
            marginLeft: 'auto',
            width: '30px',
            textAlign: 'right',
          }}
        />
      ) : null}
      {showChevron && chevronIcon ? (
        <FaIconUvilo
          icon={chevronIcon}
          sx={{
            marginLeft: item.secondaryIcon ? 0 : 'auto',
            width: '30px',
            textAlign: 'right',
          }}
        />
      ) : null}
    </>
  );
}

type SubmenuMenuItemProps = {
  item: MenuListItemType;
  selected: boolean;
  onAfterClick: (event: MouseEvent) => void;
};

/**
 * Menu parent that opens a cascading submenu of nested `items`.
 */
function SubmenuMenuItem({ item, selected, onAfterClick }: SubmenuMenuItemProps): ReactNode {
  'use memo';

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isActive, setIsActive] = useState(false);
  const open = Boolean(anchorEl);
  const isRtl = useTheme().direction === 'rtl';

  const anchorOrigin: PopoverOrigin = {
    vertical: 'top',
    horizontal: isRtl ? 'left' : 'right',
  };
  const transformOrigin: PopoverOrigin = {
    vertical: 'top',
    horizontal: isRtl ? 'right' : 'left',
  };

  const handleOpen = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleNestedAfterClick = (event: MouseEvent): void => {
    handleClose();
    onAfterClick(event);
  };

  return (
    <>
      <MenuItem
        className={clsx('submenu-menu-item', item.className)}
        disabled={item.disabled}
        divider={item.divider}
        selected={selected || open}
        onClick={handleOpen}
        aria-haspopup="menu"
        aria-expanded={open ? 'true' : undefined}
        onMouseEnter={() => {
          setIsActive(true);
        }}
        onMouseLeave={() => {
          setIsActive(false);
        }}
        onFocus={() => {
          setIsActive(true);
        }}
        onBlur={() => {
          setIsActive(false);
        }}
      >
        <MenuItemContent
          item={item}
          showChevron
          chevronIcon={isRtl ? 'chevron-left' : 'chevron-right'}
          isActive={isActive}
        />
      </MenuItem>
      <Menu
        className="menu-items-submenu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        slotProps={{
          root: {
            // Nested menu sits beside its parent; let outside clicks reach the root menu.
            sx: { pointerEvents: 'none' },
          },
          paper: {
            elevation: 5,
            sx: mergeSxProps(
              { overflowY: 'auto', pointerEvents: 'auto' },
              { ml: isRtl ? 0 : 1, mr: isRtl ? 1 : 0 },
            ),
          },
        }}
      >
        <MenuItems
          items={item.items ?? []}
          selectedItem={item.selectedItem}
          onAfterClick={handleNestedAfterClick}
        />
      </Menu>
    </>
  );
}
